// The job's billing summaries (invoices) rolled up from stored Billing rows.
//
// Billing keeps each summary as one 'invoice' row (Notes = the header blob)
// plus one 'line' row per transaction it carries, whose Notes is the frozen
// snapshot written at billing time. The snapshot is the source of truth for
// issued paper: live cost history can reload or lag, and a later rate or
// markup edit must never move a summary that already went out.
//
// This mirrors the `invTotals` memo in components/billing.js, including the
// fuel surcharge that bills on top of the equipment lines — a total without it
// does not match the summary the client got. It lives here so the Overview
// download can roll the summaries up without pulling in Billing's whole
// transaction pipeline (which needs the job's cost detail); billing.js keeps
// its own memo because it needs the per-line detail alongside the totals.
// If you change the roll-up here, change it there too.

import { pNum } from '../db/company';
import { mergeRateSchedule, DEFAULT_SETTINGS } from '../components/billingRates';
import { fuelSurcharge } from '../components/billingFuel';
import { bucketOf, normInvStatus } from '../components/billing';
import { isSummaryRow } from '../components/billingPricing';

/**
 * Roll the job's stored Billing rows up into one record per summary.
 * @param {Array} rows - forecast user-data rows at BILLING_VERSION. Rows on
 *   other tabs are ignored, so the raw load can be handed straight in.
 * @returns {Array} newest first: { no, date, status, terms, lines, hrs, cost,
 *   lineTotal, fuel, total }.
 */
export const invoiceListFrom = (rows) => {
    const billingRows = (rows || []).filter((x) => x?.Forecast_Tab === 'Billing');

    const headers = [];
    const snaps = {};
    let stored = null;
    billingRows.forEach((x) => {
        if (x.Forecast_Field_Name === 'line' && x.Forecast_Value) {
            let snap = null;
            try { snap = x.Notes ? JSON.parse(x.Notes) : null; } catch (e) { /* malformed blob — ignore */ }
            if (snap) snaps[x.Forecast_Row_ID] = { inv: x.Forecast_Value, snap };
        } else if (x.Forecast_Field_Name === 'invoice' && isSummaryRow(x)) {
            // A deleted summary is blanked rather than hard-deleted, so a row
            // with no Notes is not a summary.
            try {
                if (x.Notes) {
                    const inv = JSON.parse(x.Notes);
                    headers.push({ ...inv, status: normInvStatus(inv.status) });
                }
            } catch (e) { /* malformed blob — ignore */ }
        } else if (x.Forecast_Field_Name === 'settings') {
            try { if (x.Notes) stored = JSON.parse(x.Notes); } catch (e) { /* malformed blob — ignore */ }
        }
    });

    // Jobs that saved settings before the surcharge existed carry no fuel
    // blob — the defaults turn it on, same as the Billing tab.
    const fuelCtx = {
        settings: {
            ...DEFAULT_SETTINGS.fuel,
            ...((stored || {}).fuel || {}),
            q: { ...((((stored || {}).fuel) || {}).q || {}) },
        },
        rows: mergeRateSchedule(billingRows).fuel,
    };

    const totals = {};
    Object.entries(snaps).forEach(([key, rec]) => {
        const t = totals[rec.inv] || (totals[rec.inv] = { lines: [], hrs: 0, cost: 0, total: 0 });
        const c = pNum(rec.snap.c);
        t.lines.push({ ...rec.snap, key });
        t.hrs += pNum(rec.snap.hr);
        t.cost += c;
        // b overrides the cost×markup math for hours-billed rental lines.
        t.total += rec.snap.b != null ? pNum(rec.snap.b) : c * (1 + pNum(rec.snap.m) / 100);
    });
    Object.values(totals).forEach((t) => {
        t.lines.sort((a, b) => String(a.d).localeCompare(String(b.d)));
        t.fuel = fuelSurcharge(t.lines.map((l) => ({
            cls: bucketOf(l.ct), ct: l.ct, date: l.d, fs: l.fs, pt: !!l.pt,
            // fu: 0 froze "burns no fuel"; anything else stays in the base.
            fu: l.fu === 0 ? false : undefined,
            bill: l.b != null ? pNum(l.b) : pNum(l.c) * (1 + pNum(l.m) / 100),
        })), fuelCtx);
        t.lineTotal = t.total;
        t.total += t.fuel.amt;
    });

    return headers
        .map((h) => {
            const t = totals[h.no] || { lines: [], hrs: 0, cost: 0, lineTotal: 0, total: 0, fuel: { amt: 0 } };
            return { ...h, ...t };
        })
        .sort((a, b) => String(b.date).localeCompare(String(a.date)) || String(b.no).localeCompare(String(a.no)));
};

export const INVOICE_HEADERS = [
    'Summary #', 'Date', 'Status', 'Terms', 'Lines', 'Hours',
    'Cost', 'Lines Billed', 'Fuel Surcharge', 'Total Billed',
];

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

/**
 * One array-of-cells row per summary, headers first, for a worksheet.
 * Void summaries are kept — they're part of the job's billing record.
 * @param {Array} list - output of invoiceListFrom.
 * @returns {Array[]}
 */
export const invoiceListToSheetRows = (list) => [
    INVOICE_HEADERS,
    ...(list || []).map((v) => [
        v.no || '',
        v.date || '',
        v.status || '',
        v.terms || '',
        (v.lines || []).length,
        round2(v.hrs),
        round2(v.cost),
        round2(v.lineTotal),
        round2(v.fuel?.amt),
        round2(v.total),
    ]),
];
